/**
 * Pulls a photographer's assigned jobs down onto this device, so the
 * rest of the app (already-built, offline-first: browse, search, QR
 * card, mark complete) works exactly as it does for a locally-imported
 * job — see docs/ADR.md for that existing design, which this doesn't
 * change at all, only feeds.
 *
 * One-way, one-time per job: if a job already exists locally, it's left
 * completely alone. The server doesn't durably track per-subject
 * progress yet (see docs/PRODUCTION_READINESS.md), so once a job has
 * landed on a device, that device's own local copy is the only safe
 * source of truth for it — re-pulling could otherwise clobber progress
 * a photographer already marked. Safe to call repeatedly (e.g. a manual
 * "Refresh jobs" tap); best-effort, never throws.
 */
import { api } from '../api/client';
import { db } from '../db/db';
import type { Group, Job, Subject } from '../db/schema';

interface JobSummaryDto {
  id: string;
  name: string;
  schoolLabel: string;
  shootDate: string;
  sourceType: 'local-upload' | 'dropbox-link';
  parserAdapterId: string;
  parserAdapterVersion: string;
  importStatus: string;
  totalSubjects: number;
  completedSubjects: number;
  createdAt: string;
  retentionExpiresAt: string;
  subjectsPurged: boolean;
}

interface JobBundleDto {
  job: JobSummaryDto;
  groups: Array<{ id: string; name: string; order: number; validatedGroupQrPayload: string | null }>;
  subjects: Array<{
    id: string;
    groupId: string;
    fullName: string;
    firstName: string | null;
    lastName: string | null;
    searchNormalized: string;
    shortCode: string | null;
    internalReference: string | null;
    qrPayloadOriginal: string | null;
    qrChecksumSha256: string | null;
    sourcePage: number;
    sourceOrder: number;
    status: Subject['status'];
    excluded: boolean;
    importWarnings: unknown[];
    updatedAt: string;
  }>;
}

export interface PullSyncResult {
  /** Names of jobs newly written to this device. */
  pulled: string[];
  error?: string;
}

export async function syncAssignedJobs(): Promise<PullSyncResult> {
  try {
    const { jobs } = await api.get<{ jobs: JobSummaryDto[] }>('/api/jobs/mine', 10_000);
    const pulled: string[] = [];

    for (const summary of jobs) {
      const alreadyLocal = await db.jobs.get(summary.id);
      if (alreadyLocal) continue;

      const bundle = await api.get<JobBundleDto>(`/api/jobs/${summary.id}/bundle`, 15_000);
      await writeBundleLocally(bundle);
      pulled.push(bundle.job.name);
    }

    return { pulled };
  } catch (err) {
    return { pulled: [], error: err instanceof Error ? err.message : 'Could not check for assigned jobs.' };
  }
}

async function writeBundleLocally(bundle: JobBundleDto): Promise<void> {
  const now = new Date().toISOString();

  const job: Job = {
    id: bundle.job.id,
    name: bundle.job.name,
    schoolLabel: bundle.job.schoolLabel,
    shootDate: bundle.job.shootDate,
    sourceType: bundle.job.sourceType,
    parserAdapterId: bundle.job.parserAdapterId,
    parserAdapterVersion: bundle.job.parserAdapterVersion,
    importStatus: 'confirmed',
    totalSubjects: bundle.job.totalSubjects,
    completedSubjects: bundle.job.completedSubjects,
    availableOffline: true,
    createdAt: bundle.job.createdAt,
    updatedAt: now,
    retentionExpiresAt: bundle.job.retentionExpiresAt
  };

  const groups: Group[] = bundle.groups.map(g => ({
    id: g.id,
    jobId: bundle.job.id,
    name: g.name,
    order: g.order,
    validatedGroupQrPayload: g.validatedGroupQrPayload ?? undefined
  }));

  const subjects: Subject[] = bundle.subjects.map(s => ({
    id: s.id,
    jobId: bundle.job.id,
    groupId: s.groupId,
    fullName: s.fullName,
    firstName: s.firstName ?? undefined,
    lastName: s.lastName ?? undefined,
    searchNormalized: s.searchNormalized,
    shortCode: s.shortCode,
    internalReference: s.internalReference,
    qrPayloadOriginal: s.qrPayloadOriginal,
    qrChecksumSha256: s.qrChecksumSha256,
    sourcePage: s.sourcePage,
    sourceOrder: s.sourceOrder,
    status: s.status,
    excluded: s.excluded,
    importWarnings: (s.importWarnings ?? []) as Subject['importWarnings'],
    updatedAt: s.updatedAt
  }));

  await db.transaction('rw', db.jobs, db.groups, db.subjects, async () => {
    await db.jobs.put(job);
    await db.groups.bulkPut(groups);
    await db.subjects.bulkPut(subjects);
  });
}
